import { useState, useMemo, useRef, useEffect } from 'react';
import {
  FolderIcon,
  FolderOpenIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  FolderPlusIcon,
} from '@heroicons/react/24/outline';

/**
 * Multi-select camera-group picker: a collapsible tree with checkboxes, chips
 * for the current selection, search, and expand/collapse. Presentational only —
 * takes a FLAT `groups` list (each with `parent_id`) and rebuilds the tree.
 *
 * Shared by both dashboards (Access Control). No app services imported.
 */

/* ─── helpers ─────────────────────────────────────────────────────────── */

function buildTree(flat: any[]) {
  const map: Record<string, any> = {};
  const roots: any[] = [];
  for (const g of flat) map[g.id] = { ...g, children: [] };
  for (const g of flat) {
    if (g.parent_id && map[g.parent_id]) map[g.parent_id].children.push(map[g.id]);
    else roots.push(map[g.id]);
  }
  return roots;
}

function collectIds(node: any): string[] {
  let ids = [node.id];
  if (node.children) for (const c of node.children) ids = ids.concat(collectIds(c));
  return ids;
}

function buildPath(id: string, flatMap: Record<string, any>) {
  const parts: string[] = [];
  let cur = flatMap[id];
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parent_id ? flatMap[cur.parent_id] : null;
  }
  return parts.join(' › ');
}

function matchesSearch(name: string, query: string) {
  return name.toLowerCase().includes(query.toLowerCase());
}

function subtreeMatches(node: any, query: string): boolean {
  if (matchesSearch(node.name, query)) return true;
  if (node.children) return node.children.some((c: any) => subtreeMatches(c, query));
  return false;
}

/* ─── tree node ───────────────────────────────────────────────────────── */

function TreeNode({ group, level, selectedSet, onToggle, onToggleSubtree, expandedSet, toggleExpand, search }: any) {
  const hasChildren = group.children && group.children.length > 0;
  const isExpanded = expandedSet.has(group.id);
  const isSelected = selectedSet.has(group.id);
  const hasAccess = group.has_access !== false;

  if (search && !subtreeMatches(group, search)) return null;

  const showChildren = hasChildren && (search ? true : isExpanded);
  const allChildIds = hasChildren ? collectIds(group).filter((x: string) => x !== group.id) : [];
  const allChildrenSelected = allChildIds.length > 0 && allChildIds.every((x: string) => selectedSet.has(x));

  return (
    <div>
      <div
        className={`group/node flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${
          isSelected ? 'bg-brand/10 dark:bg-brand/20' : 'hover:bg-muted/70'
        } ${!hasAccess ? 'opacity-40 pointer-events-none' : ''}`}
        style={{ paddingLeft: `${level * 1.25 + 0.5}rem` }}
      >
        {hasChildren ? (
          <button type="button" onClick={() => toggleExpand(group.id)} className="flex-shrink-0 rounded p-0.5 hover:bg-muted">
            {showChildren
              ? <ChevronDownIcon className="h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronRightIcon className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
        ) : (
          <span className="w-[18px]" />
        )}

        <button
          type="button"
          onClick={() => onToggle(group.id)}
          className={`flex h-4 w-4 items-center justify-center rounded border flex-shrink-0 transition-colors ${
            isSelected ? 'border-brand bg-brand text-white' : 'border-border bg-input hover:border-brand/60'
          }`}
        >
          {isSelected && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
        </button>

        {showChildren
          ? <FolderOpenIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
          : <FolderIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />}

        <button
          type="button"
          onClick={() => onToggle(group.id)}
          className={`flex-1 truncate text-left transition-colors ${isSelected ? 'font-semibold text-brand' : 'text-foreground'}`}
        >
          {group.name}
        </button>

        {group.camera_count > 0 && (
          <span className="flex-shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {group.camera_count}
          </span>
        )}

        {hasChildren && (
          <button
            type="button"
            onClick={() => onToggleSubtree(group)}
            title={allChildrenSelected ? 'Deselect all children' : 'Select all children'}
            className="invisible flex-shrink-0 whitespace-nowrap rounded px-1 py-0.5 text-[10px] font-medium text-brand hover:bg-brand/10 group-hover/node:visible transition-all"
          >
            {allChildrenSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {showChildren && (
        <div>
          {group.children.map((child: any) => (
            <TreeNode
              key={child.id}
              group={child}
              level={level + 1}
              selectedSet={selectedSet}
              onToggle={onToggle}
              onToggleSubtree={onToggleSubtree}
              expandedSet={expandedSet}
              toggleExpand={toggleExpand}
              search={search}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── main component ──────────────────────────────────────────────────── */

export interface GroupPickerPanelProps {
  groups?: any[];
  selectedIds?: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  multiple?: boolean;
}

export default function GroupPickerPanel({
  groups = [],
  selectedIds = [],
  onChange,
  label = 'Assign to Groups',
  multiple = true,
}: GroupPickerPanelProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedSet, setExpandedSet] = useState<Set<string>>(() => {
    const s = new Set<string>();
    for (const g of groups) {
      if ((g.level ?? g.depth ?? 0) < 2) s.add(g.id);
    }
    return s;
  });

  const tree = useMemo(() => buildTree(groups), [groups]);
  const flatMap = useMemo(() => {
    const m: Record<string, any> = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  function toggleExpand(id: string) {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function expandAll() { setExpandedSet(new Set(groups.map((g) => g.id))); }
  function collapseAll() { setExpandedSet(new Set()); }

  function handleToggle(id: string) {
    if (multiple) {
      onChange(selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    } else {
      onChange(selectedSet.has(id) ? [] : [id]);
    }
  }

  function handleToggleSubtree(node: any) {
    const ids = collectIds(node);
    const allSelected = ids.every((id) => selectedSet.has(id));
    onChange(allSelected ? selectedIds.filter((x) => !ids.includes(x)) : [...selectedIds, ...ids.filter((x) => !selectedSet.has(x))]);
  }

  function removeSelected(id: string) { onChange(selectedIds.filter((x) => x !== id)); }
  function clearAll() { onChange([]); }

  return (
    <div ref={panelRef}>
      {label && <label className="mb-1.5 block text-[13px] font-medium text-foreground">{label}</label>}

      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-[13px] text-left transition-colors ${
          open ? 'border-brand ring-2 ring-brand-ring bg-input' : 'border-border bg-input hover:border-brand/40'
        }`}
      >
        <FolderPlusIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        {selectedIds.length === 0 ? (
          <span className="flex-1 text-muted-foreground">Click to assign groups…</span>
        ) : (
          <span className="flex-1 text-foreground">
            {selectedIds.length} group{selectedIds.length > 1 ? 's' : ''} selected
          </span>
        )}
        <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {selectedIds.length > 0 && (
        <div className="mt-1.5 flex gap-1 overflow-x-auto pb-1 scrollbar-thin">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand dark:bg-brand/20"
            >
              <span className="whitespace-nowrap">{buildPath(id, flatMap)}</span>
              <XMarkIcon
                className="h-3 w-3 flex-shrink-0 cursor-pointer hover:text-destructive transition-colors"
                onClick={() => removeSelected(id)}
              />
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-1.5 rounded-lg border border-border bg-card overflow-hidden shadow-lg animate-in fade-in slide-in-from-top-1 duration-150">
          <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
            <span className="text-[11px] text-muted-foreground">
              {selectedIds.length === 0 ? 'No groups selected' : `${selectedIds.length} selected`}
            </span>
            <div className="flex items-center gap-1">
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded px-1.5 py-0.5 text-[11px] font-medium text-destructive hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Clear all
                </button>
              )}
              <button type="button" onClick={expandAll} title="Expand all" className="rounded p-1 text-muted-foreground hover:bg-muted transition-colors">
                <ChevronDoubleDownIcon className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={collapseAll} title="Collapse all" className="rounded p-1 text-muted-foreground hover:bg-muted transition-colors">
                <ChevronDoubleUpIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="relative border-b border-border">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups…"
              className="w-full bg-transparent py-2 pl-8 pr-3 text-[13px] text-foreground placeholder-muted-foreground focus:outline-none"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 hover:bg-muted">
                <XMarkIcon className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto p-1.5">
            {tree.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-muted-foreground">No groups available</p>
            ) : (
              tree.map((group) => (
                <TreeNode
                  key={group.id}
                  group={group}
                  level={0}
                  selectedSet={selectedSet}
                  onToggle={handleToggle}
                  onToggleSubtree={handleToggleSubtree}
                  expandedSet={expandedSet}
                  toggleExpand={toggleExpand}
                  search={search}
                />
              ))
            )}
          </div>

          <div className="border-t border-border bg-muted/30 px-3 py-2 flex justify-end">
            <button
              type="button"
              onClick={() => { setOpen(false); setSearch(''); }}
              className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-dark transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
